import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import PortraitLock from './components/mobile/PortraitLock';
import { useIsMobile } from './utils/useIsMobile';
import DesktopOnlyGate from './components/admin/DesktopOnlyGate';
import Login, { FirmLogo } from './components/Login';
import NotMe from './components/NotMe';
import { useFirmIdentity, firmWebsiteLink } from './components/ComplianceContent';
import WebsiteCaptureIndicator from './components/WebsiteCaptureStatus';
import IdleWarningModal from './components/IdleWarningModal';
import { useIdleTimeout, clearIdleActivity, idleTimeoutMsForRole } from './hooks/useIdleTimeout';
import PasskeyUpsell from './components/PasskeyUpsell';
import { takePasskeySetupIntent } from './utils/passkeyDevice';
import { ToastyViewProvider } from './components/ToastyView';
import ActingGate from './components/ActingAs';
import LegalGate from './components/LegalGate';
import BudgetingNoticeGate from './components/BudgetingNoticeGate';
import { recordLogin } from './utils/passkeyDevice';
import { api, restoreSession, clearSession } from './api';
import { AppProvider, useAppData } from './AppContext';
import { FocusVisibleStyles } from './utils/buttonStyles';
import { lazyPage, FULL_SCREEN_FALLBACK } from "./utils/lazyPage";
import { BrandLoader } from "./components/BrandLoader";

// Loaded on first render; see utils/lazyPage.tsx. Dashboard too: the login
// screen shouldn't wait on the whole signed-in app.
const Dashboard = lazyPage(() => import("./components/Dashboard"), FULL_SCREEN_FALLBACK);
// Start fetching it now, alongside the session check, so a signed-in user
// isn't waiting on two downloads back to back. Login still renders first.
Dashboard.preload();
const ProspectPortal = lazyPage(() => import("./components/ProspectPortal"), FULL_SCREEN_FALLBACK);
const FormerClientPortal = lazyPage(() => import("./components/FormerClientPortal"), FULL_SCREEN_FALLBACK);
const DelegatePortal = lazyPage(() => import("./components/DelegatePortal"), FULL_SCREEN_FALLBACK);
const MobileProspectShell = lazyPage(() => import("./components/mobile/MobileProspectShell"), FULL_SCREEN_FALLBACK);
const PrivacyPolicy = lazyPage(() => import("./components/PrivacyPolicy"), FULL_SCREEN_FALLBACK);
const SmsTerms = lazyPage(() => import("./components/SmsTerms"), FULL_SCREEN_FALLBACK);
const TextUpdates = lazyPage(() => import("./components/TextUpdates"), FULL_SCREEN_FALLBACK);
const TryMyPathway = lazyPage(() => import("./components/TryMyPathway"), FULL_SCREEN_FALLBACK);
const BookCall = lazyPage(() => import("./components/BookCall"), FULL_SCREEN_FALLBACK);
const FormFillPage = lazyPage(() => import("./components/FormFillPage"), FULL_SCREEN_FALLBACK);
const SignPage = lazyPage(() => import("./components/SignPage"), FULL_SCREEN_FALLBACK);
const InternalSignPage = lazyPage(() => import("./components/SignPage").then((m) => ({ default: m.InternalSignPage })), FULL_SCREEN_FALLBACK);
const PlaidOAuthReturn = lazyPage(() => import("./components/PlaidOAuthReturn"), FULL_SCREEN_FALLBACK);
const PlaidHostedResume = lazyPage(() => import("./components/PlaidHostedResume"), FULL_SCREEN_FALLBACK);
const SecureMessageLanding = lazyPage(() => import("./components/SecureMessageLanding"), FULL_SCREEN_FALLBACK);
const MessageLinkPage = lazyPage(() => import("./components/MessageLinkPage"), FULL_SCREEN_FALLBACK);
const FormLinkPage = lazyPage(() => import("./components/FormLinkPage"), FULL_SCREEN_FALLBACK);
const UploadLinkPage = lazyPage(() => import("./components/UploadLinkPage"), FULL_SCREEN_FALLBACK);
const CheckinLinkPage = lazyPage(() => import("./components/CheckinLinkPage"), FULL_SCREEN_FALLBACK);
const ActionDigestPage = lazyPage(() => import("./components/ActionDigestPage"), FULL_SCREEN_FALLBACK);
const FirmIntakePage = lazyPage(() => import("./components/FirmIntakePage"), FULL_SCREEN_FALLBACK);
const PricingPage = lazyPage(() => import("./components/PricingPage"), FULL_SCREEN_FALLBACK);
const SecurityPage = lazyPage(() => import("./components/SecurityPage"), FULL_SCREEN_FALLBACK);
const MfaEnrollWizard = lazyPage(() => import("./components/MfaEnrollWizard"));
const QuickPanelContent = lazyPage(() => import("./components/admin/QuickPanelContent"));
const IPSTemplateV2Editor = lazyPage(() => import("./components/admin/IPSTemplateV2Editor"), FULL_SCREEN_FALLBACK);
const PlanComposerEditor = lazyPage(() => import("./components/admin/PlanComposerEditor"), FULL_SCREEN_FALLBACK);
const AcceptInvite = lazyPage(() => import("./components/AcceptInvite"), FULL_SCREEN_FALLBACK);
const ResetPassword = lazyPage(() => import("./components/ResetPassword"), FULL_SCREEN_FALLBACK);

// The public "/try" page markets the Sourdough SOFTWARE to advisors (vendor
// funnel — hardcoded Sourdough branding + the vendor's own Calendly). It must
// render ONLY on the Sourdough marketing deployment, NOT on the per-firm
// instances that ship from this same codebase (a firm's own client-acquisition
// funnel is its prospect portal, not this page). Build-time gate: the marketing
// deployment sets REACT_APP_MARKETING_SITE=1; everywhere else /try redirects home.
const IS_MARKETING_SITE = process.env.REACT_APP_MARKETING_SITE === "1";

// Nav-position params it's safe to carry through a fresh login — e.g. an
// email CTA like `?tab=wealth&wealthSub=accounts`. Deliberately excludes any
// identity/record-scoped param (cid, planEdit, docWs, docWsDoc, …): those are
// exactly the "wherever the last session left off" state handleLogin resets,
// since a stale one could leak a previous user's client into a fresh login on
// a shared browser. Mirrors the nav-vs-scoped distinction in Dashboard.tsx's
// CLIENT_SCOPED_ID_PARAMS.
const POST_LOGIN_INTENT_PARAMS = ["tab", "clientTab", "wealthSub", "workspaceSub", "recordsSub"];

/**
 * AuthenticatedApp
 *
 * The primary app shell: figure out who you are, then render Login or Dashboard.
 * This is the same logic that used to live in App.tsx before routing was added.
 *
 * Lives behind a catch-all route (`/*`) so it handles every URL except those
 * specifically claimed by other Routes (currently just /accept-invite).
 */
function AuthenticatedApp() {
  const [user, setUser] = useState<any>(null);
  const [checking, setChecking] = useState(true);
  const [mustEnrollMfa, setMustEnrollMfa] = useState(false);
  // Soft MFA nudge: a client whose enrollment deadline is approaching but
  // hasn't passed. Set from the login response, so it surfaces once per
  // sign-in and never mid-session. Null once dismissed or enrolled.
  const [mfaDeadline, setMfaDeadline] = useState<string | null>(null);
  const [canDeferMfa, setCanDeferMfa] = useState(false);  // advisor first-login "set up later"
  // null = live session. "idle" = our own 15-minute inactivity timer fired.
  // "expired" = the silent refresh failed (server-side window, revoked token,
  // password change elsewhere). The two get different copy: "you stepped away"
  // is reassuring, "something ended your session" is not.
  const [signedOutReason, setSignedOutReason] = useState<"idle" | "expired" | null>(null);
  const { setUserId } = useAppData();
  // The query string as it was when this tab first loaded — e.g. from an
  // emailed link like `?tab=wealth&wealthSub=accounts`. Captured once on
  // mount, before Login or anything else has a chance to touch the URL, so
  // handleLogin can honor that original intent instead of always resetting
  // to "/".
  const initialSearchRef = useRef(window.location.search);
  // Phone-viewport flag. Drives the prospect mobile-shell fork below,
  // mirroring how Dashboard forks clients into MobileShell. Hook runs
  // unconditionally (above the early returns) per rules-of-hooks.
  const isPhone = useIsMobile();

  useEffect(() => {
    restoreSession().then(u => {
      if (u) { setUser(u); setUserId(u.id); }
      setChecking(false);
    });
    // Listen for forced-enrollment signal raised from api.ts on 403.
    const mfaHandler = () => setMustEnrollMfa(true);
    window.addEventListener("ap:mfa-enrollment-required", mfaHandler);
    // Session expired mid-use (silent refresh failed). Only surface the
    // secure signed-out page if a session was actually active — the
    // functional setUser reads the current value without a stale closure,
    // so a boot-time signal (user still null) falls through to Login.
    const expiredHandler = () => setUser((prev: any) => { if (prev) setSignedOutReason(r => r || "expired"); return prev; });
    window.addEventListener("ap:session-expired", expiredHandler);
    return () => {
      window.removeEventListener("ap:mfa-enrollment-required", mfaHandler);
      window.removeEventListener("ap:session-expired", expiredHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (u: any, loginResponse?: any) => {
    setUser(u);
    setUserId(u.id);
    // Fresh login lands on the clients overview (no client selected), not wherever
    // the last session left off. Drop any stale ?tab=&cid= from the URL and the
    // saved per-advisor view/tab. A page RELOAD goes through restoreSession(), not
    // here, so reload still restores exactly where you were.
    //
    // Exception: if THIS tab was opened via a link carrying nav intent (an
    // email CTA like `?tab=wealth&wealthSub=accounts`), honor that instead of
    // wiping it — captured at mount into initialSearchRef, before login ever
    // touched the URL, so a stale in-session `cid`/etc. picked up mid-use can
    // never leak through here (see POST_LOGIN_INTENT_PARAMS above).
    try {
      const incoming = new URLSearchParams(initialSearchRef.current);
      const kept = new URLSearchParams();
      for (const k of POST_LOGIN_INTENT_PARAMS) {
        const v = incoming.get(k);
        if (v) kept.set(k, v);
      }
      const qs = kept.toString();
      window.history.replaceState({}, "", qs ? `/?${qs}` : "/");
      localStorage.removeItem(`mp_advisor_view_${u.id}`);
      localStorage.removeItem(`mp_dashboard_tab_${u.id}`);
    } catch { /* non-fatal */ }
    recordLogin();   // per-device login counter — gates the "set up Face ID" nudge
    if (loginResponse?.must_enroll_mfa) setMustEnrollMfa(true);
    else if (loginResponse?.mfa_deadline) setMfaDeadline(loginResponse.mfa_deadline);
    setCanDeferMfa(!!loginResponse?.can_defer_mfa);
    // The login response's `user` is a slim subset (id/email/name/role) and
    // omits is_superadmin / is_support / enabled_modules / enabled_tabs. Those
    // only come from /auth/me, so operator abilities + module gating would be
    // missing until the next full page reload — most visible via passwordless
    // login, which skips a reload. Hydrate the full identity now.
    // (restoreSession() === GET /auth/me.)
    restoreSession()
      .then(full => { if (full) { setUser(full); setUserId(full.id); } })
      .catch(() => { /* keep the slim login user; a later reload will hydrate */ });
  };

  // Advisor "set up later" — records the one-time deferral server-side, then lets
  // them into the app. Throws on a 2nd attempt (already deferred) so the wizard
  // surfaces it and hides the option.
  const handleDeferMfa = async () => {
    await api.post("/auth/mfa/defer", {});
    setCanDeferMfa(false);
    setMustEnrollMfa(false);
  };

  const handleLogout = () => {
    clearSession();
    clearIdleActivity();
    setUser(null);
  };

  // ── Inactivity sign-out ───────────────────────────────────────────────────
  // Enabled only while someone is actually signed in and past the MFA gate.
  // See hooks/useIdleTimeout.ts for why this has to live in the client.
  const handleIdleTimeout = useCallback(() => {
    // Fire-and-forget: revoke server-side, but show the signed-out page
    // immediately either way — a network failure must not leave the user
    // looking at their data after the timer said they were signed out.
    api.auth.logout().catch(() => { /* clearSession already ran locally */ });
    clearIdleActivity();
    setSignedOutReason("idle");
  }, []);

  const { warning: idleWarning, secondsRemaining: idleSeconds, stayActive } = useIdleTimeout({
    enabled: !!user && !signedOutReason,
    onIdleTimeout: handleIdleTimeout,
    idleTimeoutMs: idleTimeoutMsForRole(user?.role),
  });

  // "Stay signed in" has to do more than reset the local clock: if the access
  // token is mid-life the next call would 401 anyway. Touch the server so the
  // refresh chain rotates and last_used_at moves forward too.
  const handleStayActive = useCallback(() => {
    stayActive();
    api.get("/auth/me").catch(() => { /* a real failure surfaces via the 401 path */ });
  }, [stayActive]);

  // One element, rendered into every authenticated branch below (MFA gates
  // included — you're signed in on those screens too, and they show your name).
  // position:fixed, so where it sits in the tree doesn't matter visually.
  const idleModal = idleWarning ? (
    <IdleWarningModal
      secondsRemaining={idleSeconds}
      onStay={handleStayActive}
      onSignOut={handleLogout}
    />
  ) : null;

  if (checking) {
    return <BrandLoader fullScreen text="Sourdough" />;
  }

  // Session expired mid-use → secure signed-out interstitial instead of a
  // dead page whose data silently 401s. "Sign in again" drops to Login.
  if (signedOutReason) {
    return (
      <SessionExpiredScreen
        reason={signedOutReason}
        userName={user?.full_name || user?.first_name || user?.name}
        idleMinutes={idleTimeoutMsForRole(user?.role) / 60000}
        onSignIn={() => { clearSession(); clearIdleActivity(); setSignedOutReason(null); setUser(null); }}
      />
    );
  }

  if (!user) return <Login onLogin={handleLogin} />;
  // Forced MFA enrollment — blocks the rest of the app until complete.
  if (mustEnrollMfa) {
    return (
      <div style={{ minHeight: "100vh", background: "#F9FAFB", padding: "40px 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: "560px", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <h1 style={{ margin: "0 0 6px", fontSize: "22px", fontWeight: 800, color: "#111827", letterSpacing: "-0.01em" }}>One quick step before you start</h1>
            <p style={{ margin: 0, fontSize: "13px", color: "#6B7280" }}>
              For the security of your clients' data, we require multi-factor authentication on every {user.role === "advisor" ? "advisor" : ""} account.
            </p>
          </div>
          <MfaEnrollWizard
            onClose={handleLogout}
            // PasskeyUpsell doesn't mount while this mandatory gate is showing,
            // so a queued "user tapped passkey login with none set up" intent
            // (set on the login screen) never gets consumed here. Drain it on
            // completion so it can't replay on a later login and re-force the
            // creation wizard for a passkey this device already has.
            onComplete={() => { takePasskeySetupIntent(); setMustEnrollMfa(false); }}
            canDefer={canDeferMfa}
            onDefer={handleDeferMfa}
          />
        </div>
        {idleModal}
      </div>
    );
  }
  // Soft nudge: enrollment is coming but hasn't bitten. Same wizard as the hard
  // gate, minus the wall — "Not now" goes straight into the app. This is the
  // whole warning mechanism for the deadline: no email blast, it just meets the
  // client at the one moment they're already signed in and able to act.
  if (mfaDeadline && !mustEnrollMfa) {
    let when = "soon";
    try {
      when = new Date(mfaDeadline).toLocaleDateString(undefined, {
        month: "long", day: "numeric", year: "numeric",
      });
    } catch { /* keep "soon" — never block the app on a date parse */ }
    return (
      <div style={{ minHeight: "100vh", background: "#F9FAFB", padding: "40px 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: "560px", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <h1 style={{ margin: "0 0 6px", fontSize: "22px", fontWeight: 800, color: "#111827", letterSpacing: "-0.01em" }}>Add a second step to your sign-in</h1>
            <p style={{ margin: 0, fontSize: "13px", color: "#6B7280", lineHeight: 1.6 }}>
              Starting {when}, signing in will include one quick step to confirm it's you.
              Setting it up now takes about a minute, and you can keep using your account either way.
            </p>
          </div>
          <MfaEnrollWizard
            onClose={() => setMfaDeadline(null)}
            onComplete={() => { takePasskeySetupIntent(); setMfaDeadline(null); }}
          />
          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <button
              onClick={() => setMfaDeadline(null)}
              style={{ background: "transparent", border: "none", color: "#6B7280", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: "3px", padding: "8px 12px" }}
            >
              Not now
            </button>
          </div>
        </div>
        {idleModal}
      </div>
    );
  }

  // Prospects land in the Prospect Portal — a self-service surface for
  // tracking onboarding, viewing FAQ, uploading documents, and picking
  // a service tier when ready. They never see the advisor / client UI.
  // When the advisor graduates them to client (role flip), their next
  // /auth/me lands them in Dashboard automatically.
  // Former clients (offboarded, inside the 5-year access window) get one page:
  // receipt, documents, messages. The backend refuses every other route for
  // their session, so they must never reach Dashboard.
  if (user.role === "client" && user.is_former_client) {
    return (
      <>
        <FormerClientPortal onLogout={handleLogout} />
        {idleModal}
      </>
    );
  }

  // A client's verified POA signs in as themselves and gets one area. The
  // backend refuses every route outside /delegate/* for their session
  // (core/auth_deps.py), so they must never reach Dashboard.
  if (user.role === "delegate") {
    return (
      <>
        <DelegatePortal onLogout={handleLogout} />
        {idleModal}
      </>
    );
  }

  if (user.role === "prospect") {
    return (
      <>
        {isPhone
          ? <MobileProspectShell user={user} onLogout={handleLogout} />
          : <ProspectPortal onLogout={handleLogout} />}
        <PasskeyUpsell />
        <PortraitLock />
        {idleModal}
      </>
    );
  }
  return (
    <ToastyViewProvider>
      <LegalGate user={user}>
        <BudgetingNoticeGate user={user}>
          <ActingGate user={user} onLogout={handleLogout}>
            <Dashboard user={user} onLogout={handleLogout} />
          </ActingGate>
        </BudgetingNoticeGate>
        {/* Website capture is a server-side background job; this is the
            corner pill that says so anywhere in the app, not just on the
            Compliance page it was started from. Renders nothing unless a
            capture is actually running. */}
        {user.role === "advisor" && <WebsiteCaptureIndicator />}
        <PasskeyUpsell />
        {user.role === "client" && <PlaidHostedResume />}
        {/* Client-only: advisors keep the desktop dashboard (and its
            legitimate landscape/tablet use) regardless of viewport — see the
            matching !isAdvisor gate in Dashboard.tsx's mobile fork. */}
        {user.role !== "advisor" && <PortraitLock />}
        {idleModal}
      </LegalGate>
    </ToastyViewProvider>
  );
}

/**
 * SessionExpiredScreen
 *
 * Secure signed-out interstitial. Two ways to land here:
 *   reason="idle"    — our own 15-minute inactivity timer fired (the normal,
 *                      expected path; see hooks/useIdleTimeout.ts)
 *   reason="expired" — the silent token refresh failed: the server-side window
 *                      lapsed, the token was revoked, or the password changed
 *                      elsewhere
 * They get different copy because they mean different things to the reader.
 * "You stepped away" should feel like the product working; "your session
 * ended" should not be the phrase we use for it.
 *
 * Purely informational + CTAs — it triggers no authenticated fetches, so it
 * can't loop.
 */
function SessionExpiredScreen({
  userName,
  idleMinutes,
  onSignIn,
  reason = "expired",
}: {
  userName?: string;
  idleMinutes: number;
  onSignIn: () => void;
  reason?: "idle" | "expired";
}) {
  // Firm-driven, never hardcoded: the logo, name, website and "follow us"
  // links all come from THIS firm's identity (Settings → Firm → Branding), and
  // each link only shows when the firm has filled it in.
  const firm = useFirmIdentity();
  const firmResolved = !!firm.short_name && firm.short_name !== "Your Firm";
  const { social_url: socialUrl, social_label: socialLabel } = firm;
  const { href: websiteHref, host: websiteHost } = firmWebsiteLink(firm.website);
  const linkStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", marginTop: 10, padding: "11px 16px", background: "white", color: "var(--brand-primary)", border: "1px solid #E5E7EB", borderRadius: 4, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textDecoration: "none", boxSizing: "border-box" };
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, color-mix(in srgb, var(--brand-primary) 10%, #F9FAFB) 0%, #F9FAFB 60%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
      <div style={{ maxWidth: 420, width: "100%", background: "white", borderRadius: 4, border: "1px solid #E5E7EB", boxShadow: "0 12px 40px rgba(0,0,0,0.08)", overflow: "hidden", textAlign: "center" }}>
        <div style={{ background: "var(--brand-primary)", padding: "28px 32px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div style={{ background: "white", borderRadius: 4, padding: 4, display: "flex" }}>
            <FirmLogo size={56} />
          </div>
          {firmResolved && (
            <div style={{ color: "white", fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{firm.short_name}</div>
          )}
        </div>
        <div style={{ padding: "26px 32px 30px" }}>
          <h1 style={{ margin: "0 0 8px", fontSize: 21, fontWeight: 800, color: "#111827", letterSpacing: "-0.01em" }}>
            {userName ? `Signed out, ${String(userName).split(" ")[0]}` : "You've been signed out"}
          </h1>
          <p style={{ margin: "0 0 24px", fontSize: 13.5, color: "#6B7280", lineHeight: 1.5 }}>
            <span aria-hidden>🔒 </span>
            {reason === "idle"
              ? `You were inactive for ${idleMinutes} minutes, so we signed you out to keep your account secure.`
              : "For your security, your sign-in has expired. No data is shown until you sign in again."}
          </p>
          <button
            onClick={onSignIn}
            style={{ width: "100%", padding: "11px 16px", background: "var(--brand-primary)", color: "white", border: "none", borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
          >
            Back to login
          </button>
          {websiteHref ? (
            <a href={websiteHref} target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Visit {websiteHost} <span aria-hidden>↗</span>
            </a>
          ) : null}
          {socialUrl ? (
            <a href={socialUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
              <span aria-hidden>✨</span> {socialLabel || "Follow us"}
            </a>
          ) : null}
        </div>
      </div>
      <div style={{ marginTop: 16, fontSize: 11.5, color: "#9CA3AF" }}>Powered by Sourdough</div>
    </div>
  );
}

/**
 * AcceptInviteRoute
 *
 * Thin wrapper around AcceptInvite that gives it a navigate function so it can
 * redirect to the dashboard after successful acceptance.
 */
function AcceptInviteRoute() {
  const navigate = useNavigate();
  return <AcceptInvite onSuccess={() => navigate("/", { replace: true })} />;
}

/**
 * ResetPasswordRoute
 *
 * Same shape as AcceptInviteRoute but uses the password-reset flow.
 * Bounces to "/" on success so the user lands on the login screen.
 */
function ResetPasswordRoute() {
  const navigate = useNavigate();
  return <ResetPassword onSuccess={() => navigate("/", { replace: true })} />;
}

/**
 * NotMeRoute
 *
 * Same shape as ResetPasswordRoute. The "that wasn't me" confirmation page
 * linked from the new-device-login email — see components/NotMe.tsx and
 * backend app/api/routes/not_me.py.
 */
function NotMeRoute() {
  const navigate = useNavigate();
  return <NotMe onSuccess={() => navigate("/", { replace: true })} />;
}

/**
 * QuickPanelPopoutRoute — the 📋 Quick panel detached into its own OS window
 * (opened from QuickPanel's "pop out ↗"). Same session/auth as the main app;
 * renders the shared QuickPanelContent full-window so the advisor can keep it
 * on a second monitor while browsing anywhere. Closes itself if not an advisor.
 */
function QuickPanelPopoutRoute() {
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    restoreSession().then(u => {
      if (!u || u.role !== "advisor") { try { window.close(); } catch { /* swallow */ } }
      else setUser(u);
    });
  }, []);
  if (!user) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "white" }}>
        <div style={{ width: "20px", height: "20px", border: "2px solid #E5E7EB", borderTopColor: "var(--brand-primary)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "white" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid #F3F4F6", flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>Quick panel</span>
      </div>
      <QuickPanelContent advisorId={user.id} />
    </div>
  );
}

/**
 * IPSTemplateV2Route
 *
 * Dedicated full-screen route for the v2 template editor. The grid
 * canvas, palette, and properties panel together need more horizontal
 * room than the inside-a-tab layout gives them, so this gets its own
 * URL (and the Templates "IPS v2 (beta)" tab links here instead of
 * embedding it).
 */
function IPSTemplateV2Route() {
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Parse ?tid=N&quickmap=1 / ?tid=N&addfields=1 so the Templates tab can
  // deep-link straight into the v2 editor for a specific template, on the
  // Map Fields or Add Fields tab respectively.
  const params = new URLSearchParams(window.location.search);
  const tidStr = params.get("tid");
  const initialTemplateId = tidStr ? parseInt(tidStr, 10) : null;
  const initialElement = params.get("element") === "1";
  useEffect(() => {
    restoreSession().then(u => {
      if (!u || u.role !== "advisor") {
        navigate("/", { replace: true });
        return;
      }
      // Dedicated editor URL with no tid is redundant with the Templates
      // tab's embedded list view. Bounce there instead of showing two
      // copies of the same table.
      if (initialTemplateId == null) {
        navigate("/?tab=library&librarySub=templates&templatesSegment=ips_v2", { replace: true });
        return;
      }
      setReady(true);
    });
  }, [navigate, initialTemplateId]);
  if (!ready) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "white" }}>
        <div style={{ width: "20px", height: "20px", border: "2px solid #E5E7EB", borderTopColor: "var(--brand-primary)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (isMobile) return <DesktopOnlyGate title="The IPS template editor" onBack={() => navigate("/?tab=library&librarySub=templates&templatesSegment=ips_v2")} />;
  return <IPSTemplateV2Editor initialTemplateId={initialTemplateId} initialElement={initialElement} />;
}

/**
 * Plan Composer editor route — the standalone Canva-style composer for
 * client-facing planning documents. Mirrors IPSTemplateV2Route but is
 * decoupled from the signing lifecycle and backed by the plan_composer_*
 * tables. Lives at its own URL so the editor gets full-screen room.
 */
function PlanComposerRoute() {
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const params = new URLSearchParams(window.location.search);
  const tidStr = params.get("tid");
  const initialTemplateId = tidStr ? parseInt(tidStr, 10) : null;
  const initialElement = params.get("element") === "1";
  // Deep-link to a page within a plan: ?tid=<plan>&page=<element>.
  const pageStr = params.get("page");
  const initialPageId = pageStr ? parseInt(pageStr, 10) : null;
  useEffect(() => {
    restoreSession().then(u => {
      if (!u || u.role !== "advisor") {
        navigate("/", { replace: true });
        return;
      }
      if (initialTemplateId == null) {
        navigate("/?tab=library&librarySub=templates&templatesSegment=plan_composer", { replace: true });
        return;
      }
      setReady(true);
    });
  }, [navigate, initialTemplateId]);
  if (!ready) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "white" }}>
        <div style={{ width: "20px", height: "20px", border: "2px solid #E5E7EB", borderTopColor: "var(--brand-primary)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (isMobile) return <DesktopOnlyGate title="The plan composer" onBack={() => navigate("/?tab=library&librarySub=templates&templatesSegment=plan_composer")} />;
  return <PlanComposerEditor initialTemplateId={initialTemplateId} initialElement={initialElement} initialPageId={initialPageId} />;
}

// The public marketing hosts (bare domain + www) show the /try landing at
// their root, while the app itself lives on app.<domain>. Kept as an explicit
// allow-list so app.breaderthanever.com (and localhost / *.vercel.app dev
// hosts) always fall through to the authenticated app at "/".
function isMarketingRootHost(): boolean {
  try {
    const h = window.location.hostname;
    return h === "breaderthanever.com" || h === "www.breaderthanever.com";
  } catch {
    return false;
  }
}

export default function App() {
  const marketingRoot = isMarketingRootHost();
  return (
    <AppProvider>
      <FocusVisibleStyles />
      <BrowserRouter>
        <Routes>
          {/* Marketing front door: bare domain / www show the landing at "/". */}
          {marketingRoot && <Route path="/" element={<TryMyPathway />} />}
          <Route path="/accept-invite/:token" element={<AcceptInviteRoute />} />
          <Route path="/reset-password/:token" element={<ResetPasswordRoute />} />
          <Route path="/not-me/:token" element={<NotMeRoute />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/sms-terms" element={<SmsTerms />} />
          <Route path="/text-updates" element={<TextUpdates />} />
          <Route path="/try" element={IS_MARKETING_SITE ? <TryMyPathway /> : <Navigate to="/" replace />} />
          <Route path="/book" element={(IS_MARKETING_SITE || marketingRoot) ? <BookCall /> : <Navigate to="/" replace />} />
          <Route path="/forms/fill/:id" element={<FormFillPage />} />
          <Route path="/sign/:token" element={<SignPage />} />
          <Route path="/internal-sign/:eid" element={<InternalSignPage />} />
          <Route path="/popout/quickpanel" element={<QuickPanelPopoutRoute />} />
          <Route path="/admin/ips-templates-v2" element={<IPSTemplateV2Route />} />
          <Route path="/admin/plan-composer" element={<PlanComposerRoute />} />
          <Route path="/plaid-oauth" element={<PlaidOAuthReturn />} />
          <Route path="/m/:token" element={<SecureMessageLanding />} />
          <Route path="/digest/:token" element={<ActionDigestPage />} />
          <Route path="/msg/:token" element={<MessageLinkPage />} />
          <Route path="/f/:token" element={<FormLinkPage />} />
          {/* No-login upload for someone outside the app (a POA, CPA, attorney). */}
          <Route path="/u/:token" element={<UploadLinkPage />} />
          <Route path="/c/:token" element={<CheckinLinkPage />} />
          {/* Public firm-onboarding questionnaire (operator-hosted). No session:
              the answering firm's own instance may not be provisioned yet. */}
          <Route path="/firm-intake/:token" element={<FirmIntakePage />} />
          {/* Public price list. Unauthenticated and unconditional — a firm being
              courted should be able to look up the number without an invite. */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* Public security page. Same posture as /pricing: unauthenticated and
              unconditional, because the compliance consultant a firm forwards it
              to has no invite and no session. */}
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}
